import React, { Component } from 'react';
import Button from 'react-bootstrap/Button';

// Browsers only hand out a push subscription in response to a user gesture, so
// this has to be a button — it cannot be enabled automatically on page load.
class PushToggle extends Component {

    state = { status: 'idle', message: '', alert: null };

    isArabic = () => String(this.props.locale || '').startsWith('ar');
    tx = (en, ar) => this.isArabic() ? ar : en;

    messageText = key => ({
        unsupported: this.tx('This browser does not support notifications.', 'هذا المتصفح لا يدعم الإشعارات.'),
        blocked: this.tx('Notifications are blocked in this browser’s site settings.', 'الإشعارات محظورة في إعدادات الموقع بالمتصفح.'),
        denied: this.tx('Notification permission was denied.', 'تم رفض إذن الإشعارات.'),
        on: this.tx('Notifications are on for this browser.', 'الإشعارات مفعّلة في هذا المتصفح.'),
        noKey: this.tx('Push key is not configured on the server.', 'مفتاح الإشعارات غير مضبوط على الخادم.'),
        saveFailed: this.tx('Could not save the notification subscription.', 'تعذّر حفظ اشتراك الإشعارات.'),
        enableFailed: this.tx('Could not enable notifications.', 'تعذّر تفعيل الإشعارات.'),
        off: this.tx('Notifications are off for this browser.', 'الإشعارات متوقفة في هذا المتصفح.'),
        disableFailed: this.tx('Could not turn notifications off.', 'تعذّر إيقاف الإشعارات.')
    }[key] || key || '');

    componentDidMount() {
        if (typeof window === 'undefined') return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            this.setState({ status: 'unsupported', message: 'unsupported' });
            return;
        }
        if (Notification.permission === 'denied') {
            this.setState({ status: 'denied', message: 'blocked' });
            return;
        }
        navigator.serviceWorker.addEventListener('message', this.handleWorkerMessage);

        navigator.serviceWorker.getRegistration('/push-sw.js').then((registration) => {
            if (!registration) return;
            // Pull in a newer worker instead of running whatever was cached when
            // notifications were first switched on.
            registration.update();
            registration.pushManager.getSubscription().then((subscription) => {
                if (!subscription) return;
                this.setState({ status: 'on', message: 'on' });
                // The browser keeps its subscription even if saving it server-side
                // failed, which would otherwise leave the button showing "on" with
                // nothing stored. Re-saving is an upsert, so it is safe every time.
                this.save(subscription).catch(() => this.setState({ status: 'error', message: 'saveFailed' }));
            });
        }).catch(() => { });
    }

    componentWillUnmount() {
        if (this.titleTimer) clearInterval(this.titleTimer);
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener('message', this.handleWorkerMessage);
        }
    }

    handleWorkerMessage = (event) => {
        if (!event.data || event.data.type !== 'booking-notification') return;
        this.beep();
        // An in-page banner, because the OS notification is at the mercy of macOS
        // and Chrome display settings that this app cannot control.
        this.setState({
            alert: {
                title: event.data.title || 'New booking',
                body: event.data.body || ''
            }
        });
        this.flashTitle(event.data.title || 'New booking');
    }

    // Alternate the tab title so a background tab is noticeable without any
    // dependency on OS notifications.
    flashTitle = (text) => {
        if (this.titleTimer) clearInterval(this.titleTimer);
        const original = document.title;
        let toggle = false;
        this.titleTimer = setInterval(() => {
            document.title = toggle ? original : `🔔 ${text}`;
            toggle = !toggle;
        }, 1000);

        const stop = () => {
            clearInterval(this.titleTimer);
            this.titleTimer = null;
            document.title = original;
            window.removeEventListener('focus', stop);
        };
        window.addEventListener('focus', stop);
    }

    dismissAlert = () => {
        if (this.titleTimer) {
            clearInterval(this.titleTimer);
            this.titleTimer = null;
        }
        this.setState({ alert: null });
    }

    // Short chime via WebAudio — no asset to ship, and it works even when the OS
    // notification sound for Chrome is switched off.
    beep = () => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            const ctx = new AudioCtx();
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();
            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, ctx.currentTime);
            oscillator.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
            gain.gain.setValueAtTime(0.25, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + 0.5);
            oscillator.onended = () => ctx.close();
        } catch (error) { /* audio is a nicety, never break the page for it */ }
    }

    // Base64url VAPID key -> Uint8Array, the format PushManager requires.
    urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = window.atob(base64);
        const output = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
        return output;
    }

    save = async (subscription) => {
        const response = await fetch('/admin-push/subscribe', {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ subscription })
        });
        return response.json();
    }

    enable = async () => {
        this.setState({ status: 'working', message: '' });
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                this.setState({ status: 'denied', message: 'denied' });
                return;
            }

            const keyResponse = await fetch('/admin-push/key', { credentials: 'include' });
            const { publicKey } = await keyResponse.json();
            if (!publicKey) {
                this.setState({ status: 'error', message: 'noKey' });
                return;
            }

            const registration = await navigator.serviceWorker.register('/push-sw.js');
            await navigator.serviceWorker.ready;

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(publicKey)
            });

            const saved = await this.save(subscription);

            if (saved.status === 200) {
                this.setState({ status: 'on', message: 'on' });
            } else {
                this.setState({ status: 'error', message: 'saveFailed' });
            }
        } catch (error) {
            this.setState({ status: 'error', message: 'enableFailed' });
        }
    }

    disable = async () => {
        this.setState({ status: 'working', message: '' });
        try {
            const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
            const subscription = registration && await registration.pushManager.getSubscription();
            if (subscription) {
                await fetch('/admin-push/unsubscribe', {
                    method: 'post',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ endpoint: subscription.endpoint })
                });
                await subscription.unsubscribe();
            }
            this.setState({ status: 'idle', message: 'off' });
        } catch (error) {
            this.setState({ status: 'error', message: 'disableFailed' });
        }
    }

    render() {
        const { status, message, alert } = this.state;
        const { compact } = this.props;
        const working = status === 'working';
        const unavailable = status === 'unsupported' || status === 'denied';
        const onLabel = this.tx('Turn off booking notifications', 'إيقاف إشعارات الطلبات');
        const offLabel = this.tx('Enable booking notifications', 'تفعيل إشعارات الطلبات');
        const label = working
            ? this.tx('Working…', 'جارٍ التنفيذ…')
            : (status === 'on' ? onLabel : offLabel);
        const detail = this.messageText(message);

        return (
            <div className={compact ? 'push-toggle compact' : null}
                style={compact ? null : { marginBottom: '15px' }}>
                {alert &&
                    <button type="button"
                        onClick={this.dismissAlert}
                        aria-label={this.tx('Dismiss booking notification', 'إغلاق إشعار الطلب')}
                        style={{
                            position: 'fixed',
                            top: '20px',
                            insetInlineEnd: '20px',
                            zIndex: 70,
                            maxWidth: '360px',
                            padding: '16px 20px',
                            border: 0,
                            borderRadius: '10px',
                            background: '#1f2937',
                            color: '#fff',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                            cursor: 'pointer',
                            textAlign: 'start'
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>🔔 {alert.title}</div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>{alert.body}</div>
                        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>
                            {this.tx('Click to dismiss', 'انقر للإغلاق')}
                        </div>
                    </button>
                }
                {compact
                    ? <button className="btn ghost sm" type="button"
                        onClick={status === 'on' ? this.disable : this.enable}
                        disabled={working || unavailable} title={detail || label} aria-label={label}>
                        <span aria-hidden="true">🔔</span>
                        <span className="push-label">{status === 'on'
                            ? this.tx('Notifications on', 'الإشعارات مفعّلة')
                            : label}</span>
                      </button>
                    : (status === 'on'
                        ? <Button variant="secondary" onClick={this.disable} disabled={working}>{label}</Button>
                        : <Button variant="primary" onClick={this.enable} disabled={working || unavailable}>{label}</Button>)
                }
                {!compact && detail && <span style={{ marginInlineStart: '10px' }}>{detail}</span>}
            </div>
        );
    }
}

export default PushToggle;
